import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import storageService from '../../../../utils/storageService';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTowerBroadcast,
  faCalendarDay,
  faCheckCircle,
  faTriangleExclamation,
  faMapMarkerAlt,
  faUserTie,
  faEye,
  faArrowsRotate,
  faImage,
  faClock,
  faBuildingUser,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import CommonTable from '../../table/ComonTable';
import CommonModal from '../../commonmodal/CommonModal';
import { roundInspectionApi } from '../roundInspectionApi';
import apiServices from '../../../../services/apiService';

const LiveOperationsMonitor = ({ siteId }) => {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailModalItem, setDetailModalItem] = useState(null);

  // Vendor Context Detection
  const { user: authUser } = useSelector((state) => state.auth || {});
  const userRoleId = Number(storageService.getItem("roleId") || authUser?.roleId || localStorage.getItem("roleId") || 0);
  const personTypeId = Number(storageService.getItem("personTypeId") || authUser?.personTypeId || localStorage.getItem("personTypeId") || 0);
  const isVendorLogin = userRoleId === 7 || personTypeId === 4;
  const loggedInVendorId = isVendorLogin
    ? Number(storageService.getItem("vendorId") || authUser?.vendorId || authUser?.id || authUser?.Id || authUser?.userId || storageService.getItem("employeeCode") || localStorage.getItem("vendorId") || 0)
    : null;

  const [vendors, setVendors] = useState([]);
  const [selectedVendorFilter, setSelectedVendorFilter] = useState('');

  // Load active vendors for Site Admin
  useEffect(() => {
    if (isVendorLogin || !siteId) return;
    const loadVendors = async () => {
      try {
        const res = await apiServices({
          url: `/vendor-management/vendor/GetVendors?siteId=${siteId}&pageSize=100`,
          method: 'GET'
        });
        const list = res?.data?.data || (Array.isArray(res?.data) ? res.data : []);
        setVendors(list);
      } catch (e) {
        console.warn('Failed to fetch vendors:', e);
      }
    };
    loadVendors();
  }, [siteId, isVendorLogin]);

  const fetchVisits = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const vendorParam = isVendorLogin
        ? loggedInVendorId
        : (selectedVendorFilter && selectedVendorFilter !== 'IN_HOUSE' ? Number(selectedVendorFilter) : null);
      const res = await roundInspectionApi.getLiveVisits(siteId, null, selectedDate, vendorParam);
      const data = res?.data?.data || res?.data || [];
      if (Array.isArray(data)) {
        setVisits(data);
      } else {
        setVisits([]);
      }
    } catch (err) {
      toast.error('Failed to load live monitor stream: ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  }, [siteId, selectedDate, isVendorLogin, loggedInVendorId, selectedVendorFilter]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  const filteredVisits = visits.filter((v) => {
    if (selectedVendorFilter === 'IN_HOUSE') {
      return !v.vendorId;
    }
    if (selectedVendorFilter) {
      return String(v.vendorId) === String(selectedVendorFilter);
    }
    return true;
  });

  // Compute metrics based on filtered stream
  const totalScans = filteredVisits.length;
  const verifiedScans = filteredVisits.filter(
    (v) => v.isGpsVerified === true || v.isGpsVerified === 1
  ).length;
  const flaggedScans = totalScans - verifiedScans;
  const complianceRate =
    totalScans > 0 ? Math.round((verifiedScans / totalScans) * 100) : 100;

  // Table Configuration
  const tableHeaders = [
    { label: 'TIME', key: 'visitTime', align: 'center' },
    { label: 'STAFF OFFICER', key: 'staff', align: 'left' },
    { label: 'AGENCY / VENDOR', key: 'vendorName', align: 'center' },
    { label: 'CHECKPOINT LOCATION', key: 'checkpoint', align: 'left' },
    { label: 'ROUND TITLE', key: 'round', align: 'left' },
    { label: 'GEOFENCE VERIFICATION', key: 'geofence', align: 'center' },
    { label: 'STATUS', key: 'status', align: 'center' },
    { label: 'INSPECT', key: 'actions', align: 'center' },
  ];

  const renderCell = (key, row, header) => {
    switch (key) {
      case 'visitTime':
        return (
          <div className="text-center">
            <div className="font-bold text-xs text-textprimary flex items-center justify-center gap-1">
              <FontAwesomeIcon icon={faClock} className="text-textsecondary text-[10px]" />
              {row.visitTime
                ? new Date(row.visitTime).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : 'Just now'}
            </div>
            <div className="text-[10px] text-textsecondary">
              {row.visitTime ? new Date(row.visitTime).toLocaleDateString() : selectedDate}
            </div>
          </div>
        );
      case 'staff':
        return (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-textsecondary text-xs">
              <FontAwesomeIcon icon={faUserTie} />
            </div>
            <div>
              <div className="font-bold text-xs text-textprimary">
                {row.staffName || `Staff #${row.staffUserId || 'N/A'}`}
              </div>
              <div className="text-[10px] text-textsecondary">{row.staffRoleName || 'Field Officer'}</div>
            </div>
          </div>
        );
      case 'vendorName': {
        const vendorObj = vendors.find((v) => String(v.vendorId || v.id) === String(row.vendorId));
        const effectiveName = row.vendorName || vendorObj?.vendorName || (row.vendorId ? `Vendor #${row.vendorId}` : null);
        return effectiveName ? (
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200 inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faBuildingUser} className="text-[9px]" />
            {effectiveName}
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
            In-House
          </span>
        );
      }
      case 'checkpoint':
        return (
          <div>
            <div className="font-bold text-xs text-textprimary">{row.locationName}</div>
            <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-[10px] font-bold text-slate-800 dark:text-slate-200">
              {row.locationCode}
            </span>
          </div>
        );
      case 'round':
        return (
          <span className="px-2.5 py-1 rounded bg-slate-100 dark:bg-slate-800 text-xs font-medium text-textprimary">
            {row.roundName || 'Ad-Hoc Inspection'}
          </span>
        );
      case 'geofence':
        return row.isGpsVerified ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <FontAwesomeIcon icon={faCheckCircle} />
            Within Geofence ({row.distanceMeters ?? 0}m)
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            Out of Bounds ({row.distanceMeters ?? 'N/A'}m)
          </span>
        );
      case 'status':
        return (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
            {row.status || 'COMPLETED'}
          </span>
        );
      case 'actions':
        return (
          <button
            onClick={() => setDetailModalItem(row)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
          >
            <FontAwesomeIcon icon={faEye} />
            Inspect Form
          </button>
        );
      default:
        return row[key] || '-';
    }
  };

  const headerActions = (
    <div className="flex items-center gap-3">
      {/* Date Picker Input */}
      <div className="flex items-center gap-2">
        <label className="text-xs font-semibold text-textsecondary flex items-center gap-1">
          <FontAwesomeIcon icon={faCalendarDay} />
          Date:
        </label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="border border-gray-300 dark:border-bordercolor rounded-lg px-2.5 py-1.5 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>

      {/* Vendor Filter (Site Admin Only) */}
      {!isVendorLogin && (
        <div className="flex items-center gap-1.5 bg-white dark:bg-cardbg border border-gray-200 dark:border-bordercolor rounded-lg px-2.5 py-1.5 shadow-sm text-xs">
          <FontAwesomeIcon icon={faFilter} className="text-textsecondary text-xs" />
          <select
            value={selectedVendorFilter}
            onChange={(e) => setSelectedVendorFilter(e.target.value)}
            className="bg-transparent border-none text-xs font-medium text-textprimary focus:outline-none cursor-pointer"
          >
            <option value="">All Vendors & In-House</option>
            <option value="IN_HOUSE">In-House Only</option>
            {vendors.map((v) => (
              <option key={v.vendorId || v.id} value={v.vendorId || v.id}>
                {v.vendorName || v.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Refresh Button */}
      <button
        onClick={fetchVisits}
        className="border border-bordercolor px-3 py-1.5 bg-cardbg hover:bg-gray-50 text-textprimary text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
      >
        <FontAwesomeIcon icon={faArrowsRotate} />
        Refresh
      </button>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Top 4 KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Scans */}
        <div className="bg-slate-50 dark:bg-cardbg border border-bordercolor rounded-xl p-4 flex items-center justify-between shadow-xs">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-textsecondary">Total Scans</div>
            <div className="text-2xl font-bold text-textprimary mt-1">{totalScans}</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-slate-200/60 dark:bg-slate-800 flex items-center justify-center text-textsecondary">
            <FontAwesomeIcon icon={faTowerBroadcast} />
          </div>
        </div>

        {/* GPS Verified */}
        <div className="bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900 rounded-xl p-4 flex items-center justify-between shadow-xs">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-400">
              GPS Verified
            </div>
            <div className="text-2xl font-bold text-emerald-900 dark:text-emerald-300 mt-1">{verifiedScans}</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-emerald-700">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
        </div>

        {/* Flagged / Spoofed */}
        <div className="bg-rose-50/60 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900 rounded-xl p-4 flex items-center justify-between shadow-xs">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-rose-800 dark:text-rose-400">
              Flagged / Spoofed
            </div>
            <div className="text-2xl font-bold text-rose-900 dark:text-rose-300 mt-1">{flaggedScans}</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center text-rose-700">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </div>
        </div>

        {/* Compliance Rate */}
        <div className="bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-900 rounded-xl p-4 flex items-center justify-between shadow-xs">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-800 dark:text-indigo-400">
              Compliance Rate
            </div>
            <div className="text-2xl font-bold text-indigo-900 dark:text-indigo-300 mt-1">{complianceRate}%</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-700">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
          </div>
        </div>
      </div>

      {/* Table Component */}
      <div className="border border-gray-200 dark:border-bordercolor rounded-xl overflow-hidden shadow-sm">
        <CommonTable
          loading={loading}
          tableHeading="Live Inspection & Audit Logs"
          headers={tableHeaders}
          data={filteredVisits}
          renderCell={renderCell}
          headerAction={headerActions}
          onPreview={(row) => setDetailModalItem(row)}
          customActions={[
            {
              label: 'Inspect Form',
              icon: faEye,
              onClick: (row) => setDetailModalItem(row),
            },
          ]}
        />
      </div>

      {/* Checklist Inspection Details Modal using CommonModal */}
      <CommonModal
        isOpen={Boolean(detailModalItem)}
        onClose={() => setDetailModalItem(null)}
        title="Patrol Inspection Audit Record"
        subTitle="Detailed review of answers, gauge telemetry readings, and captured photos."
        footerContent={
          <div className="flex justify-end">
            <button
              onClick={() => setDetailModalItem(null)}
              className="px-4 py-2 text-xs font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700"
            >
              Close
            </button>
          </div>
        }
      >
        {detailModalItem && (
          <div className="space-y-4">
            {/* Header Summary Box */}
            <div className="p-3.5 bg-gray-50 dark:bg-dashboardbg rounded-xl border border-gray-200 dark:border-bordercolor flex justify-between items-start">
              <div>
                <div className="font-bold text-xs text-textprimary">{detailModalItem.locationName}</div>
                <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-[10px] font-bold text-slate-800 dark:text-slate-200 mt-1 inline-block">
                  {detailModalItem.locationCode}
                </span>
              </div>
              <div className="text-right">
                <div className="font-bold text-xs text-textprimary">
                  {detailModalItem.staffName || 'Field Officer'}
                </div>
                <div className="text-[10px] text-textsecondary">
                  {detailModalItem.visitTime ? new Date(detailModalItem.visitTime).toLocaleTimeString() : ''}
                </div>
              </div>
            </div>

            {/* Officer Selfie if available */}
            {detailModalItem.selfieUrl && (
              <div className="p-3 bg-indigo-50/50 dark:bg-indigo-950/20 rounded-xl border border-indigo-100 dark:border-indigo-900/40 flex items-center gap-3">
                <img
                  src={detailModalItem.selfieUrl}
                  alt="Staff Selfie"
                  className="w-14 h-14 rounded-lg object-cover border border-indigo-200 shadow-xs cursor-pointer hover:opacity-90"
                  onClick={() => window.open(detailModalItem.selfieUrl, '_blank')}
                />
                <div>
                  <div className="text-xs font-bold text-indigo-950 dark:text-indigo-200">Staff Selfie Verified</div>
                  <div className="text-[11px] text-indigo-600 dark:text-indigo-400">Captured at checkpoint location</div>
                  <a
                    href={detailModalItem.selfieUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] font-semibold text-indigo-700 underline hover:text-indigo-900 mt-0.5 inline-block"
                  >
                    Open Full Image &rarr;
                  </a>
                </div>
              </div>
            )}

            {/* Responses List */}
            <div>
              <span className="text-xs font-bold text-textsecondary uppercase tracking-wider block mb-2">
                Checklist Responses
              </span>

              {Array.isArray(detailModalItem.responses) && detailModalItem.responses.length > 0 ? (
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {detailModalItem.responses.map((r, i) => (
                    <div
                      key={i}
                      className="p-3 border border-gray-200 dark:border-bordercolor rounded-xl bg-white dark:bg-cardbg shadow-xs"
                    >
                      <div className="text-xs text-textsecondary mb-1 font-medium">
                        {r.fieldLabel || r.questionLabel || r.fieldKey || 'Task'}
                      </div>
                      <div className="font-bold text-xs text-textprimary">
                        {r.valueBoolean === true || r.responseValue === 'true' || r.responseValue === 'PASS' ? (
                          <span className="text-emerald-600 flex items-center gap-1">
                            <FontAwesomeIcon icon={faCheckCircle} />
                            PASS / YES
                          </span>
                        ) : r.valueBoolean === false || r.responseValue === 'false' || r.responseValue === 'FAIL' ? (
                          <span className="text-rose-600 flex items-center gap-1">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            FAIL / NO
                          </span>
                        ) : r.valueNumber !== undefined && r.valueNumber !== null ? (
                          <span className="text-blue-600 font-mono font-bold">
                            {r.valueNumber}
                          </span>
                        ) : (
                          (r.valueText && !r.valueText.startsWith('http') && !r.valueText.startsWith('data:image')) 
                            ? r.valueText 
                            : (r.responseValue || '-')
                        )}
                      </div>

                      {(r.photoUrl || r.photoFileId) && (
                        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-bordercolor flex items-center justify-between">
                          <img
                            src={r.photoUrl || r.photoFileId}
                            alt="Captured Photo"
                            className="w-16 h-16 rounded-lg object-cover border border-gray-200 shadow-xs cursor-pointer hover:opacity-90"
                            onClick={() => window.open(r.photoUrl || r.photoFileId, '_blank')}
                          />
                          <a
                            href={r.photoUrl || r.photoFileId}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700 shadow-2xs"
                          >
                            <FontAwesomeIcon icon={faImage} className="text-indigo-600" />
                            View Captured Photo
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 border border-gray-200 dark:border-bordercolor rounded-xl bg-gray-50/50 text-xs text-textsecondary">
                  Checkpoint was scanned successfully without additional checklist questions.
                </div>
              )}
            </div>
          </div>
        )}
      </CommonModal>
    </div>
  );
};

export default LiveOperationsMonitor;
